import React from "react";
import Head from "next/head";

import { IBlogPost } from "@/services/wordpress";

/**
 * SEO meta for a single blog post, passed through from Yoast so titles,
 * descriptions, canonicals, and schema stay editable in WordPress.
 */
export const BlogPostHead: React.FC<{ post: IBlogPost }> = ({ post }) => {
  const yoast = post.yoast || {};
  const canonical = yoast.canonical || `https://magicul.io/blog/${post.slug}`;
  const title = yoast.title || `${post.title} | Magicul`;
  const description = yoast.description || yoast.og_description || "";
  const ogImage = yoast.og_image?.[0];

  return (
    <Head>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="article" />
      <meta property="og:title" content={yoast.og_title || title} />
      {(yoast.og_description || description) && (
        <meta
          property="og:description"
          content={yoast.og_description || description}
        />
      )}
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Magicul" />
      {ogImage?.url && <meta property="og:image" content={ogImage.url} />}
      {ogImage?.width && (
        <meta property="og:image:width" content={String(ogImage.width)} />
      )}
      {ogImage?.height && (
        <meta property="og:image:height" content={String(ogImage.height)} />
      )}

      {yoast.article_published_time && (
        <meta
          property="article:published_time"
          content={yoast.article_published_time}
        />
      )}
      {yoast.article_modified_time && (
        <meta
          property="article:modified_time"
          content={yoast.article_modified_time}
        />
      )}

      <meta
        name="twitter:card"
        content={yoast.twitter_card || "summary_large_image"}
      />

      {yoast.schema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(yoast.schema) }}
        />
      )}
    </Head>
  );
};
